// Налаштування додатку — редагування глобальних змінних (наразі: посилання на підтримку).
import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

const SETTING_LABELS: Record<string, { label: string; hint: string; placeholder: string }> = {
  supportUrl: {
    label: 'Чат підтримки',
    hint: 'Посилання на чат підтримки (наприклад, https://t.me/your_support). Якщо порожнє — кнопка в боті не показується.',
    placeholder: 'https://t.me/your_support',
  },
  newsChannelUrl: {
    label: 'Канал з новинами',
    hint: 'Посилання на Telegram-канал з новинами/оновленнями (наприклад, https://t.me/your_channel). Якщо порожнє — кнопка в боті не показується.',
    placeholder: 'https://t.me/your_channel',
  },
  wayforpayMerchantAccount: {
    label: 'WayForPay — Merchant Account',
    hint: 'Логін мерчанта (Кабінет WayForPay → Налаштування мерчанта → Реквізити → Merchant Login).',
    placeholder: 'test_merch_n1',
  },
  wayforpayMerchantSecretKey: {
    label: 'WayForPay — Secret Key',
    hint: 'Секретний ключ мерчанта (Кабінет → Налаштування мерчанта → Реквізити → Secret key). НЕ розголошувати.',
    placeholder: 'flk3409refn54t54t*FNJRET',
  },
  wayforpayMerchantDomain: {
    label: 'WayForPay — Merchant Domain',
    hint: 'Домен сайту як в кабінеті WayForPay. Без http://, без слешу. Наприклад: testypdr.example.com',
    placeholder: 'testypdr.example.com',
  },
  smtpHost: {
    label: 'SMTP — Host (email-логін)',
    hint: 'Сервер SMTP. Brevo: smtp-relay.brevo.com',
    placeholder: 'smtp-relay.brevo.com',
  },
  smtpPort: {
    label: 'SMTP — Port',
    hint: 'Зазвичай 587 (STARTTLS) або 465 (TLS). Порожньо = 587.',
    placeholder: '587',
  },
  smtpUser: {
    label: 'SMTP — User (логін)',
    hint: 'Логін SMTP. У Brevo — твій email/SMTP-логін з розділу SMTP & API.',
    placeholder: 'you@example.com',
  },
  smtpPass: {
    label: 'SMTP — Password (ключ)',
    hint: 'SMTP-ключ (НЕ пароль від акаунту). Генерується в кабінеті Brevo → SMTP & API.',
    placeholder: 'xsmtpsib-...',
  },
  smtpFrom: {
    label: 'SMTP — Відправник (From)',
    hint: 'Від кого лист. Має бути верифікований у Brevo. Напр.: Тести ПДР <noreply@pdrtest.online>',
    placeholder: 'Тести ПДР <noreply@pdrtest.online>',
  },
};

export function AdminSettings() {
  const [items, setItems] = useState<Array<{ key: string; value: string }> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await api.admin.listSettings();
      setItems(list);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Не вдалось завантажити'));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function save(key: string, value: string) {
    setSavingKey(key);
    try {
      const res = await api.admin.updateSetting(key, value);
      setItems((prev) => (prev ? prev.map((s) => (s.key === key ? res : s)) : prev));
      setSavedKey(key);
      setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 1500);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не вдалось зберегти');
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="space-y-3 p-4">
      {loading && <Loader />}
      {error && <ErrorView error={error} onRetry={load} />}
      {items && (
        <>
          {items.map((s) => (
            <SettingField
              key={s.key}
              k={s.key}
              value={s.value}
              saving={savingKey === s.key}
              saved={savedKey === s.key}
              onSave={(v) => save(s.key, v)}
            />
          ))}
        </>
      )}
    </div>
  );
}

function SettingField({
  k,
  value,
  saving,
  saved,
  onSave,
}: {
  k: string;
  value: string;
  saving: boolean;
  saved: boolean;
  onSave: (v: string) => void;
}) {
  const meta = SETTING_LABELS[k] ?? { label: k, hint: '', placeholder: '' };
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const dirty = draft !== value;

  return (
    <section className="card space-y-2">
      <label className="block text-sm font-medium">{meta.label}</label>
      <input
        type="url"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={meta.placeholder}
        className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-tg-button"
      />
      {meta.hint && <p className="text-xs text-tg-hint">{meta.hint}</p>}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onSave(draft)}
          disabled={!dirty || saving}
          className="btn-primary"
        >
          {saving ? 'Збереження…' : 'Зберегти'}
        </button>
        {saved && <span className="text-sm text-tg-success">✓ Збережено</span>}
        {dirty && !saving && !saved && (
          <button onClick={() => setDraft(value)} className="text-sm text-tg-hint">
            Скасувати
          </button>
        )}
      </div>
    </section>
  );
}
